import type {
  GraphCollectionResponse,
  GraphGroup,
  GraphListResult,
  GraphServicePrincipal,
} from '@ns/plugin-msgraph-proxy-client';

import { GRAPH_VERSION } from './directory-query';
import type { EntraGroup, EntraServicePrincipal } from './types';

/** A display option with one category that does not belong in form data. */
export interface ServicePrincipalOption extends EntraServicePrincipal {
  readonly servicePrincipalType: string;
}

/** Options a Graph page yields, and how many of its rows could not be offered. */
export interface DirectoryOptions<TOption> {
  /** Rows of the page left out because a field the option needs was missing or not a string. */
  readonly droppedRowCount: number;
  /** The complete options, in the order Graph sent their rows. */
  readonly options: readonly TOption[];
}

/** Turns a validated Graph groups page into complete, flat picker options and counts the rows left out. */
export function buildGroupOptions(
  response: GraphCollectionResponse<GraphGroup>,
): DirectoryOptions<EntraGroup> {
  const options = response.value.flatMap(item => {
    if (typeof item.id !== 'string' || typeof item.displayName !== 'string') {
      return [];
    }
    return [
      {
        displayName: item.displayName,
        id: item.id,
        type: resolveDirectoryType(
          item['@odata.type'],
          response['@odata.context'],
          'groups',
        ),
      },
    ];
  });
  return { droppedRowCount: response.value.length - options.length, options };
}

/** Turns a validated Graph service-principals page into display options and counts the rows left out. */
export function buildServicePrincipalOptions(
  response: GraphCollectionResponse<GraphServicePrincipal>,
): DirectoryOptions<ServicePrincipalOption> {
  const options = response.value.flatMap(item => {
    if (
      typeof item.id !== 'string' ||
      typeof item.appId !== 'string' ||
      typeof item.displayName !== 'string'
    ) {
      return [];
    }
    return [
      {
        appId: item.appId,
        displayName: item.displayName,
        id: item.id,
        servicePrincipalType: item.servicePrincipalType ?? 'Unknown category',
        type: resolveDirectoryType(
          item['@odata.type'],
          response['@odata.context'],
          'servicePrincipals',
        ),
      },
    ];
  });
  return { droppedRowCount: response.value.length - options.length, options };
}

/** What the group picker reads from a Graph page: its options, the dropped-row count, and the continuation. */
export function selectGroupPage(
  page: GraphListResult<GraphGroup>,
): DirectoryOptions<EntraGroup> & Pick<GraphListResult<GraphGroup>, 'more'> {
  return { ...buildGroupOptions(page.response), more: page.more };
}

/** What the service-principal picker reads from a Graph page: its options, the dropped-row count, and the continuation. */
export function selectServicePrincipalPage(
  page: GraphListResult<GraphServicePrincipal>,
): DirectoryOptions<ServicePrincipalOption> &
  Pick<GraphListResult<GraphServicePrincipal>, 'more'> {
  return { ...buildServicePrincipalOptions(page.response), more: page.more };
}

/** The keys a group value carries, in form data and in a restored value. */
export const GROUP_FIELDS = {
  displayName: true,
  id: true,
  type: true,
} as const satisfies Record<keyof EntraGroup, true>;

/** The keys a service-principal value carries, in form data and in a restored value. */
export const SERVICE_PRINCIPAL_FIELDS = {
  appId: true,
  displayName: true,
  id: true,
  type: true,
} as const satisfies Record<keyof EntraServicePrincipal, true>;

/** Writes exactly `id`, `appId`, `displayName` and `type` of an option into form data. The display-only category stays behind. */
export function buildServicePrincipalFormData(
  option: ServicePrincipalOption,
): EntraServicePrincipal {
  return {
    appId: option.appId,
    displayName: option.displayName,
    id: option.id,
    type: option.type,
  };
}

type DirectoryCollection = 'groups' | 'servicePrincipals';

const ODATA_TYPE_BY_COLLECTION = {
  groups: '#microsoft.graph.group',
  servicePrincipals: '#microsoft.graph.servicePrincipal',
} as const satisfies Record<DirectoryCollection, string>;

/** Whether a form value's `type` is a non-blank string. The validator compares it with the offered row. */
export function isNonBlankType(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/** The item `@odata.type` as Graph sent it, else the type of the confirmed page collection, else the literal `unknown`. */
export function resolveDirectoryType(
  itemType: string | undefined,
  odataContext: string | undefined,
  expectedCollection: DirectoryCollection,
): string {
  if (itemType !== undefined && itemType.trim() !== '') return itemType;
  return isTrustedCollectionContext(odataContext, expectedCollection)
    ? ODATA_TYPE_BY_COLLECTION[expectedCollection]
    : 'unknown';
}

/** Whether the page `@odata.context` is Graph's own, for this version and exactly the expected collection. */
function isTrustedCollectionContext(
  odataContext: string | undefined,
  expectedCollection: DirectoryCollection,
): boolean {
  if (odataContext === undefined) return false;
  try {
    const url = new URL(odataContext);
    if (
      url.hostname !== 'graph.microsoft.com' ||
      url.protocol !== 'https:' ||
      url.username !== '' ||
      url.password !== '' ||
      url.search !== '' ||
      url.pathname !== `/${GRAPH_VERSION}/$metadata`
    ) {
      return false;
    }
    return (
      /^#([^(/]+)(?:\([^()]*\))?$/.exec(url.hash)?.[1] === expectedCollection
    );
  } catch {
    return false;
  }
}
